import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  BackArrow,
  Input,
  Select,
  CheckBox,
  RevealButton,
  Button,
  TopProgress
} from '../../../../components'
import {
  Container,
  TitleBig,
  FormWrapper,
  CreateContainer,
  Info,
  InfoText,
  DarkForm
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import Label from '../../../../components/label'
import { flavorService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { InfoContainer, InfoTitle } from '../../../../components/info-block/styles'
import { icons } from '../../../../icons'
import { usePageTitle } from '../../../../context/navigationContext'

const CreateFlavor:FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const {flavorId} = useParams()
  usePageTitle(t("PAGE_TITLE_FLAVOR_CREATE"))
  const [flavor, setFlavor] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [isPublic, setIsPublic] = useState<boolean>(true)
  const [showInFleio, setShowInFleio] = useState<boolean>(true)
  const [useForDbaas, setUseForDbaas] = useState<boolean>(false)
  const [outOfStock, setOutOfStock] = useState<boolean>(false)
  const [regions, setRegions] = useState<any[]>([])

  const [initialValues, setInitialValues] = useState<any>({
    name:'',
    display_name:'',
    description:'',
    vcpus:1,
    memory_mb:512,
    root_gb:1,
    ephemeral_gb:0,
    swap:0,
    region:'',
    preserve_id:true,
    flavor_id:null
  })

  const onSubmit = (values:any) => {
    setLoading(true)
    const data = {
      ...values,
      is_public:isPublic,
      show_for_end_user:showInFleio,
      use_for_dbaas:useForDbaas,
      out_of_stock:outOfStock
    }

    if(!location.pathname.includes('create') && flavor){
      flavorService.updateFlavor(flavor.id, data)
      .then((res) => {
        setLoadingComplete(true)
        setTimeout(() => {
          navigate('/openstack/flavors')
          resetForm()
          setLoadingComplete(false)
          setLoading(false)
        },1000)
      })
      .catch((err:any) => {
        setLoading(false)
        viewAlert({
          severity:'error',
          message: err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`
        })
      })
    } else {
      flavorService.createFlavor(data)
      .then((res) => {
        setLoadingComplete(true)
        setTimeout(() => {
          navigate('/openstack/flavors')
          resetForm()
          setLoadingComplete(false)
          setLoading(false)
        },1000)
      })
      .catch((err:any) => {
        setLoading(false)
        viewAlert({
          severity:'error',
          message: err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`
        })
      })
    }
  }

  const getCreateOptions = async () => {
    try{
      if(flavorId){
        const flavorRes = await flavorService.getFlavor(flavorId)
        setFlavor(flavorRes?.data)
        setInitialValues({
          name:flavorRes?.data?.name,
          display_name:flavorRes?.data?.display_name,
          description:flavorRes?.data?.description,
          vcpus:flavorRes?.data?.vcpus,
          memory_mb:flavorRes?.data?.memory_mb,
          root_gb:flavorRes?.data?.root_gb,
          ephemeral_gb:flavorRes?.data?.ephemeral_gb,
          swap:flavorRes?.data?.swap,
          region:flavorRes?.data?.region,
        })
        setIsPublic(flavorRes?.data?.is_public)
        setShowInFleio(flavorRes?.data?.show_for_end_user)
        setUseForDbaas(flavorRes?.data?.use_for_dbaas)
        setOutOfStock(flavorRes?.data?.out_of_stock)
      }
      const resCreateOptions = await flavorService.getCreateOptions()
      setRegions(resCreateOptions.data.regions?.map((region:any) => {
        return {
          ...region,
          label:region?.id,
          value:region?.id
        }
      }))
      await setPageLoading(false)
    } catch (e) {
      console.error('Error loading create options:', e)
    }
  }

  useEffect(() => {
    getCreateOptions()
  },[])

  const regName = /^[a-zA-Z0-9\\!\"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{\|}~ ]+$/

  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    handleChange,
    setFieldValue,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string()
      .matches(regName,`${t("ONLY_ENGLISH_LETTERS")}`)
      .max(60, `${t("STRING_VALIDATE_MAX")} 60 ${t("CHARACTERS")}`)
      .required(`${t('REQUIRED_FIELD')}`),
      display_name: yup.string()
      .max(60, `${t("STRING_VALIDATE_MAX")} 60 ${t("CHARACTERS")}`),
      description: yup.string().max(255, `${t("STRING_VALIDATE_MAX")} 255 ${t("CHARACTERS")}`),
      vcpus: yup.number()
      .min(1, `${t("MIN_VALUE")} 1`)
      .required(`${t('REQUIRED_FIELD')}`),
      memory_mb: yup.number()
      .min(1, `${t("MIN_VALUE")} 1`)
      .required(`${t('REQUIRED_FIELD')}`),
      root_gb: yup.number()
      .min(1, `${t("MIN_VALUE")} 1`)
      .required(`${t('REQUIRED_FIELD')}`),
      ephemeral_gb: yup.number()
      .min(0, `${t("MIN_VALUE")} 0`),
      swap: yup.number()
      .min(0, `${t("MIN_VALUE")} 0`),
      region: yup.string().required(`${t('REQUIRED_FIELD')}`),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit,
  });

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  const isEdit = !location.pathname.includes('create') && flavor
  if(pageLoading) return <TopProgress loading={pageLoading}/>
  return(
    <CreateContainer>
      <Container>
        <BackArrow fallbackPath="/openstack/flavors"/>
        <TitleBig style={{margin:'0px'}}>{isEdit ? t("EDIT_FLAVOR") + ' ' + (flavor?.name || flavor?.id) : t("CREATE_A_NEW_FLAVOR")}</TitleBig>
        <FormWrapper>
          <Input
            fieldName='name'
            title={t("NAME_FLAVOR")}
            placeholder=''
            customStyles={{width:'100%'}}
            {...formikProps}
          />
          <Input
            fieldName='display_name'
            title={t("DISPLAY_NAME")}
            placeholder=''
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            required
            toolTip={false}
            data={regions}
            fieldName='region'
            selectedValue={values['region']}
            onChange={(e) => handleChange('region')(e)}
            {...formikProps}
            title={t("REGION")}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            fieldName='vcpus'
            title={t("VCPUS")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
          <Input
            fieldName='memory_mb'
            title={t("MEMORY_MB")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            fieldName='root_gb'
            title={t("ROOT_DISK_GB")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
          <Input
            fieldName='ephemeral_gb'
            title={t("EPHEMERAL_DISK_GB")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            fieldName='swap'
            title={t("SWAP_MB")}
            placeholder=''
            type="number"
            customStyles={{width:'100%'}}
            {...formikProps}
          />
        </FormWrapper>
        <CheckBox
          value={isPublic}
          onChange={() => setIsPublic(!isPublic)}
          title={t("IS_PUBLIC")}
        />
        <CheckBox
          value={showInFleio}
          onChange={() => setShowInFleio(!showInFleio)}
          title={t("SHOW_FOR_END_USER")}
        />
        <CheckBox
          value={useForDbaas}
          onChange={() => setUseForDbaas(!useForDbaas)}
          title={t("USE_FOR_DBAAS")}
        />
        <CheckBox
          value={outOfStock}
          onChange={() => setOutOfStock(!outOfStock)}
          title={t("OUT_OF_STOCK")}
        />
        <div style={{ marginTop:"20px"}}>
          <RevealButton
            title={t("ADDITIONAL_SETTINGS")}
          >
            <Input
              isTextArea
              fieldName='description'
              title={t("DESCRIPTION")}
              customStyles={{maxWidth:'550px', height:'200px'}}
              placeholder=''
              {...formikProps}
            />
          </RevealButton>
        </div>
        <div style={{display:'flex', marginTop:'20px'}}>
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            customStyles={{marginRight:"10px"}}
            onClick={handleSubmit}
            title={isEdit ? t("EDIT") : t("CREATE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate("/openstack/flavors")}
            title={t("CANCEL")}
          />
        </div>
      </Container>
      {isEdit ? null :
        <Container style={{marginTop:'91px'}}>
          <Info>
            <Label reverse title={t("FLAVOR_SPECIFICATIONS")}/>
            <InfoText>
              {t("FLAVOR_SPECIFICATIONS_INFO_1")}
            </InfoText>
            <InfoText style={{marginTop:"10px"}}>
              {t("FLAVOR_SPECIFICATIONS_INFO_2")}
            </InfoText>
          </Info>
          <Info style={{marginTop:"20px"}}>
            <Label reverse title={t("PUBLIC_FLAVORS")}/>
            <InfoText>
              {t("PUBLIC_FLAVORS_INFO")}
            </InfoText>
          </Info>
        </Container>
      }
    </CreateContainer>
  )
}

export default CreateFlavor
